import React, { useEffect, useMemo, useRef } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { ItemListOption } from '@/contexts/item-context-types';
import { OrderableItemList } from '@/components/orders/orderable-item-list';
import { OrderableItemListItemWithRowQuantity } from '@/components/orders/orderable-item-list-item';
import { RecyclerListRef } from '@/components/recycler-list';
import { removeAdComponents } from '@/contexts/item-context-utils';
import { ScannerType } from './scanner-types';
import { useAdaptAndGroupVariantPacks } from '@/hooks/use-adapt-and-group-item-list-options';
import { useItemDetailContext } from '@/contexts/item-detail-context';
import { useNewOrderActionsContext } from '@/contexts/new-order-actions-context';
import { useScannerContext } from '@/contexts/scanner-context';
import { useShowListView } from '@/hooks/use-show-list-view';
import { View } from 'react-native';

interface Props {
  scannerType: ScannerType;
}

export const AddedToCartList: React.FC<Props> = ({ scannerType }) => {
  const [styles] = useStyles();
  // Do not sort unavailable items to bottom since scan results are always on top
  const adaptAndGroupVariantPacks = useAdaptAndGroupVariantPacks(false);
  const { selectItemListOption } = useItemDetailContext();
  const { debouncedUpdateQuantity } = useNewOrderActionsContext();
  const { cartVariantPacks, newVariantPackUUIDs, removeFromOrder } =
    useScannerContext();
  const { showListView } = useShowListView();

  const listRef = useRef<RecyclerListRef<ItemListOption> | null>(null);

  useEffect(() => {
    listRef.current?.scrollToTop();
  }, [cartVariantPacks]);

  const listData = useMemo(
    () =>
      adaptAndGroupVariantPacks(cartVariantPacks ?? []).map((option) => ({
        ...option,
        metadata: {
          ...option.metadata!,
          skus: option.metadata!.skus.map(removeAdComponents),
        },
      })),
    [adaptAndGroupVariantPacks, cartVariantPacks],
  );

  return (
    <View style={styles.base}>
      <OrderableItemList
        highlightedItemValues={
          newVariantPackUUIDs ? [...newVariantPackUUIDs] : []
        }
        listData={listData}
        listItemProps={{
          listItemStyle: styles.itemStyleOverrides,
          onSelect: (item) =>
            selectItemListOption(item, { source: scannerType }),
          renderMainContent: (option) => {
            return (
              <OrderableItemListItemWithRowQuantity
                contentStyle={styles.listItemContentStyle}
                disableTextInput
                isFocused={false}
                onOptionsPress={(item) =>
                  selectItemListOption(item, { source: scannerType })
                }
                option={option}
                source={scannerType}
                updateQuantity={(updateQuantityData) => {
                  debouncedUpdateQuantity(updateQuantityData, {
                    source: scannerType,
                  });

                  if (updateQuantityData.newQuantity === 0) {
                    removeFromOrder(option.metadata!.variantPackUUID);
                  }
                }}
              />
            );
          },
        }}
        listRef={listRef}
        showListView={showListView}
      />
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    flex: 1,
  },
  itemStyleOverrides: {
    backgroundColor: theme.colors.transparent,
    paddingHorizontal: 8,
    paddingVertical: 0,
  },
  listItemContentStyle: {
    backgroundColor: theme.colors.transparent,
  },
}));
